import React, {useEffect, useMemo, useRef} from 'react';
import {ThreadItem} from '../api/types';
import {buildArtifactMap} from '../artifacts';
import {ArtifactMapContext} from './ArtifactViews';
import ThreadItemRenderer from './ThreadItemRenderer';
import {StarterPrompt} from '../AssistantIntegration';

interface MessageListProps {
    items: ThreadItem[];
    pendingApprovalItemId: string | null;
    isApprovalSubmitting: boolean;
    onApprovalDecision: (
        approvalRequestItemId: string,
        approved: boolean
    ) => void;
    /** Suggestions for an empty conversation; clicking one sends it. */
    starterPrompts?: StarterPrompt[];
    onStarter?: (prompt: string) => void;
}

/**
 * Scrolls to the bottom on new content unless the user has scrolled away to read history.
 */
const MessageList: React.FC<MessageListProps> = ({
    items,
    pendingApprovalItemId,
    isApprovalSubmitting,
    onApprovalDecision,
    starterPrompts,
    onStarter
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const stickToBottomRef = useRef(true);

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const distanceFromBottom =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
        stickToBottomRef.current = distanceFromBottom < 80;
    };

    useEffect(() => {
        const container = containerRef.current;
        if (container && stickToBottomRef.current) {
            container.scrollTop = container.scrollHeight;
        }
    }, [items]);

    // Files produced by the thread's runtime calls, so messages can resolve
    // sandbox: links to them
    const artifactMap = useMemo(() => buildArtifactMap(items), [items]);

    return (
        <ArtifactMapContext.Provider value={artifactMap}>
            <div
                ref={containerRef}
                onScroll={handleScroll}
                className="flex-grow-1 overflow-auto pe-1"
                // Long links and ids in replies wrap rather than widen the panel
                style={{minHeight: 0, overflowWrap: 'anywhere'}}
            >
                {items.length === 0 &&
                    onStarter &&
                    starterPrompts &&
                    starterPrompts.length > 0 && (
                        <div className="small p-2">
                            <p className="text-muted mb-2">
                                Ask anything, or try one of these:
                            </p>
                            <div className="d-flex flex-column gap-2">
                                {starterPrompts.map(starter => (
                                    <button
                                        key={starter.label}
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary text-start"
                                        onClick={() =>
                                            onStarter(starter.prompt)
                                        }
                                    >
                                        {starter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                {items.map(item => (
                    <ThreadItemRenderer
                        key={item.id}
                        item={item}
                        pendingApprovalItemId={pendingApprovalItemId}
                        isApprovalSubmitting={isApprovalSubmitting}
                        onApprovalDecision={onApprovalDecision}
                    />
                ))}
            </div>
        </ArtifactMapContext.Provider>
    );
};

export default MessageList;
